
import { Component, Vue, Prop } from 'vue-property-decorator';
import ExpandableList from '@/components/shared/ExpandableList.vue';

@Component<RotationalMenuItemList>({
	components: {
		ExpandableList
	}
})
export default class RotationalMenuItemList extends Vue {
	@Prop({ type: Object, required: true }) private menu!: Menu;
	@Prop({ type: Array, required: true }) private selectedItems!: RotationalMenuPayloadItem[];
	@Prop({ type: String, required: false }) private searchString!: string;

	private expanded: boolean = false;

	/**
	 * Get filtered menu with both items and sections filtered based on search string
	 *
	 * @return {Menu}
	 */
	private get filteredMenu(): Menu {
		if (!this.searchString) {
			return this.menu;
		}

		const filteredMenuItems = this.filterItems(this.menu.items);

		// Filter section items
		const filteredSections = this.menu.sections?.map((section: MenuSection) => {
			const filteredSectionItems = this.filterItems(section.items);
			return { ...section, items: filteredSectionItems };
		}).filter((section: MenuSection) => (section.items?.length || 0) > 0) || [];

		return {
			...this.menu,
			items: filteredMenuItems,
			sections: filteredSections
		};
	}

	private get numberOfSelectedItems(): number {
		return this.selectedItems.filter((selectedItem: RotationalMenuPayloadItem) => selectedItem.menu_id === this.menu.id).length;
	}

	private get selectAllDisabled(): boolean {
		return this.numberOfSelectedItems === this.filteredMenu.items?.length;
	}

	/**
	 * Get total items count across all sections
	 *
	 * @return {number}
	 */
	private get totalItemsCount(): number {
		if (this.menu.sections?.length) {
			return (this.filteredMenu.sections || []).reduce((total, section) => total + (section.items?.length || 0), 0);
		}
		return this.filteredMenu.items?.length || 0;
	}

	/**
	 * Handle item click to ensure menu_id is set
	 *
	 * @param {MenuItem} item
	 * @return {void}
	 */
	private handleItemClick(item: MenuItem): void {
		if (!this.menu.id) {
			return;
		}
		const itemWithMenuId = { ...item, menu_id: this.menu.id };
		this.$emit('update-selected-items', [itemWithMenuId]);
	}

	/**
	 * Filters items by search string
	 *
	 * @param {MenuItem[]} items
	 * @return {MenuItem[]}
	 */
	private filterItems(items: MenuItem[] | undefined): MenuItem[] {
		return items?.filter((item: MenuItem) => item.name.toLowerCase().includes(this.searchString?.toLowerCase() || '')) || [];
	}

	/**
	 * Find all items for the current menu that are not selected and emit event to update selected items
	 * If a search string is present, only unselected items that match the search string will be selected
	 * Works with both sectioned and non-sectioned menus
	 *
	 * @return {void}
	 */
	private selectAllItems(): void {
		if (!this.menu.id) {
			return;
		}

		let allItems: MenuItem[] = [];
		if (this.menu.sections?.length) {
			// Get all items from all sections and add menu_id
			allItems = (this.filteredMenu.sections || []).flatMap((section: MenuSection) =>
				(section.items || []).map((item: MenuItem) => ({ ...item, menu_id: this.menu.id! }))
			);
		}
		else {
			// Get items directly from menu and add menu_id
			allItems = (this.filteredMenu.items || []).map((item: MenuItem) => ({ ...item, menu_id: this.menu.id! }));
		}

		const unselectedItems = allItems.filter((item: MenuItem) => {
			return !this.selectedItems.find((selectedItem: RotationalMenuPayloadItem) =>
				selectedItem.menu_id === this.menu.id &&
				(selectedItem.menu_item_id === item.id || selectedItem.section_item_id === item.id)
			);
		});

		if (unselectedItems.length > 0) {
			this.$emit('update-selected-items', unselectedItems);
		}
	}

	/**
	 * Checks if an item is selected
	 * This is used to determine the checkbox state
	 *
	 * @param {MenuItem} item
	 * @return {boolean}
	 */
	private isSelected(item: MenuItem): boolean {
		return !!this.selectedItems.find((selectedItem: RotationalMenuPayloadItem) => selectedItem.section_item_id === item.id || selectedItem.menu_item_id === item.id);
	}
}
